// apps/merchant-v3/src/lib/useTabAlert.ts
//
// dbg-20260706 (support/notification tab alert) — reflects the merchant's
// total unread count in the BROWSER TAB and chimes when a new one arrives.
//
//   • document.title → `(N) Printway Merchant v3` when N unread (badge on the
//     tab, visible even when the tab is in the background). Base title when 0.
//   • On an unread INCREASE (new notification pushed live via the WS
//     `notification.new` event), plays a short Web-Audio chime. Producing
//     audio makes the browser show its native per-tab speaker/mute control —
//     the "loa" the user clicks to silence the tab.
//
// Source of truth is the shared `['notifications','unread-count']` query
// (same slice the bell + Center header read), so the tab badge, the topbar
// bell, and the sidebar badge always agree. Support ticket replies now feed
// this count too (BE: ticket.handler → notificationService.fanout).
//
// The chime honours a persisted opt-out (`isAlertSoundEnabled`) in addition
// to the browser's own tab-mute, defaulting ON.
//
// vg-binding: CONTEXT:P15.D-18   (Task 29 — canonical signal-only notification.new; legacy notification.create RETIRED, G-35)
// vg-binding: CONTEXT:P15.D-08   (audit-zero DoD — 0 FE `.on('notification.create')`)

import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { setTitleUnreadBadge } from '@printway/ui';

import { getUnreadCount } from '@/services/notificationsClient';
import { useWsEvent } from '@/lib/websocket/WsProvider';

const ALERT_SOUND_KEY = 'merchant-v3.alert.sound_enabled';
const UNREAD_QUERY_KEY = ['notifications', 'unread-count'] as const;

/** Persisted opt-out for the new-item chime. Default ON (opt-out). */
export function isAlertSoundEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const v = window.localStorage.getItem(ALERT_SOUND_KEY);
    return v === null ? true : v !== 'false';
  } catch {
    return true;
  }
}

export function setAlertSoundEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ALERT_SOUND_KEY, enabled ? 'true' : 'false');
  } catch {
    /* private mode — ignore */
  }
}

/**
 * Global side-effect hook — mount ONCE (AppShell). Keeps the browser tab in
 * sync with the merchant's unread total (no sound — admin-only, dbg-20260710).
 */
export function useTabAlert(): void {
  const queryClient = useQueryClient();

  const { data: unread = 0 } = useQuery<number>({
    queryKey: UNREAD_QUERY_KEY,
    queryFn: () => getUnreadCount(),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  // Live push — a new notification (incl. a support reply fanned out by the
  // BE) invalidates the shared count so the tab badge + chime react at once.
  // Canonical signal-only `notification.new` (P15.D-18); the legacy
  // `notification.create` listener was RETIRED in Task 29 (G-35).
  useWsEvent('notification.new', () => {
    void queryClient.invalidateQueries({ queryKey: UNREAD_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  });

  // Tab-title badge (shared @printway/ui helper — same one the admin support
  // hook uses). Favicon dot dropped as redundant next to the title count.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    setTitleUnreadBadge(unread);
  }, [unread]);

  // dbg-20260710: sound is admin-only — the merchant tab keeps the unread
  // title badge but no longer chimes. `prevUnread` retained in case the sound
  // is reinstated behind a toggle later.
  const prevUnread = useRef<number | null>(null);
  useEffect(() => {
    prevUnread.current = unread;
  }, [unread]);
}
